import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * LanguageGate — Full-screen first-visit language selection.
 * Shown ONLY when localStorage has no `seikat_lang` value.
 * Two cinematic options: Indonesia (default brand language) or English.
 * Stored in localStorage; never shown again unless cleared.
 */
export default function LanguageGate() {
  const { languageReady, setLanguage } = useLanguage();

  if (languageReady) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="language-gate"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, y: -40 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-0 z-[120] bg-seikat-ink overflow-hidden grain"
        data-testid="language-gate"
      >
        {/* Ambient backdrop */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1647644520784-7b6ad81c9efd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-seikat-ink via-seikat-ink/80 to-seikat-ink-2" />
        </div>

        {/* Falling petals decoration */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 10 }).map((_, i) => (
            <span
              key={i}
              className={`petal ${i % 2 === 0 ? "petal--lime" : ""}`}
              style={{
                left: `${(i * 11) % 100}%`,
                ["--delay"]: `${(i * 0.7) % 5}s`,
                ["--dur"]: `${9 + (i % 4)}s`,
                ["--drift"]: `${(i % 2 === 0 ? 1 : -1) * (30 + (i % 3) * 20)}px`
              }}
            />
          ))}
        </div>

        {/* Brand */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="absolute top-7 left-7 lg:top-9 lg:left-12 z-10"
        >
          <span className="font-heading text-2xl tracking-tight text-seikat-bone">
            Seikat<span className="text-seikat-pink">.</span>
          </span>
        </motion.div>

        {/* Center content */}
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="text-center max-w-2xl mb-14"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="w-10 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">Pilih Bahasa · Choose Language</span>
              <span className="w-10 h-px bg-seikat-pink" />
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-seikat-bone leading-[0.95] tracking-tight">
              Selamat datang. <span className="italic text-seikat-pink">Welcome.</span>
            </h1>
            <p className="font-body text-seikat-bone/70 mt-5 text-base lg:text-lg font-light leading-relaxed">
              Mari mulai dengan satu bahasa. <span className="italic">Let's begin with one language.</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="grid sm:grid-cols-2 gap-4 lg:gap-6 w-full max-w-2xl"
          >
            <LanguageButton
              code="id"
              flag="🇮🇩"
              title="Bahasa Indonesia"
              subtitle="Rangkaian puisi dalam bahasa kita."
              onChoose={() => setLanguage("id")}
              accent="pink"
              testid="lang-gate-id"
            />
            <LanguageButton
              code="en"
              flag="🇬🇧"
              title="English"
              subtitle="A weave of poetry, in English."
              onChoose={() => setLanguage("en")}
              accent="lime"
              testid="lang-gate-en"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="font-body text-seikat-bone-muted text-xs tracking-[0.25em] uppercase mt-14"
          >
            Pilihan dapat diganti kapan saja · You can change this anytime
          </motion.p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function LanguageButton({ code, flag, title, subtitle, onChoose, accent, testid }) {
  const accentRing = accent === "lime" ? "hover:border-seikat-lime/60" : "hover:border-seikat-pink/60";
  const accentText = accent === "lime" ? "group-hover:text-seikat-lime" : "group-hover:text-seikat-pink";
  return (
    <button
      onClick={onChoose}
      data-testid={testid}
      data-magnet
      className={`group relative overflow-hidden glass-dark-strong rounded-3xl p-8 lg:p-10 text-left transition-all duration-500 hover:bg-white/[0.08] border border-white/8 ${accentRing}`}
    >
      <div className="flex items-start justify-between mb-6">
        <span className="text-4xl leading-none">{flag}</span>
        <span className="font-body text-[10px] tracking-[0.25em] uppercase text-seikat-bone-muted">{code.toUpperCase()}</span>
      </div>
      <h3 className={`font-heading text-3xl lg:text-4xl text-seikat-bone transition-colors ${accentText}`}>
        {title}
      </h3>
      <p className="font-body text-sm text-seikat-bone/60 mt-2 italic">{subtitle}</p>
      <div className={`mt-8 inline-flex items-center gap-2 text-xs tracking-widest uppercase text-seikat-bone-muted ${accentText} transition-colors`}>
        <span>{code === "id" ? "Lanjutkan" : "Continue"}</span>
        <span className="transition-transform group-hover:translate-x-1">→</span>
      </div>
    </button>
  );
}
